import { memo, useState, useCallback, useEffect, useMemo } from 'react'
import {
  AlertTriangle, BarChart3, CalendarCheck, CalendarDays, Clock3, Film, HardDrive, Images, LayoutGrid, Layers, Megaphone, Music4, Settings, Smartphone,
} from 'lucide-react'
import {
  useStore, useActiveChannelId, useBridgeConnected, useWsConnected, useWsStateReady, useWsEverReady,
  useUnreadAlerts, usePlayoutState, useAuthUser,
} from '../../store/index.js'
import { useChannelList } from '../../hooks/useChannels.js'
import { usePlayoutSync } from '../../hooks/usePlayoutSync.js'
import { useLiveSchedule } from '../../hooks/useSchedule.js'
import { useMobileOperatorMode } from '../../hooks/useMobileOperatorMode.js'
import { useTheme } from '../../hooks/useTheme.js'
import { AgendaView }      from '../agenda/AgendaView.js'
import { ProgramsView }    from '../programs/ProgramsView.js'
import { JinglesView }     from '../jingles/JinglesView.js'
import { CgOverlaysView }  from '../cg/CgOverlaysView.js'
import { AdsView }         from '../ads/AdsView.js'
import { InfoserviceView } from '../infoservice/InfoserviceView.js'
import { ReportsView }     from '../reports/ReportsView.js'
import { PremiereCalendarView } from '../production/PremiereCalendarView.js'
import { SettingsView }    from '../settings/SettingsView.js'
import { MobileOperatorView } from '../mobile/MobileOperatorView.js'
import { OPEN_SETTINGS_TAB_EVENT, type OpenSettingsTabDetail } from '../../lib/appNavigation.js'
import { LiveViewer } from '../monitor/LiveViewer.js'
import { GridTemplatesView } from '../templates/GridTemplatesView.js'
import { MediaLibraryView } from '../media/MediaLibraryView.js'
import { ClockAdminView } from '../clock/ClockAdminView.js'
import { AppHeader } from './AppHeader.js'
import { OnAirBanner } from '../monitor/OnAirBanner.js'
import { ToastContainer }  from './ToastContainer.js'
import { OperatorAlertPopups } from './OperatorAlertPopups.js'
import { CommandPalette, type PaletteTarget } from '../common/CommandPalette.js'
import type { Permission, Program }  from '../../types/index.js'
import { ANTENNA_ELEMENT_PROGRAM_TYPES } from '../../lib/utils.js'
import {
  canPresentWorkspace,
  dataAvailabilityState,
  type DataAvailabilityState,
} from '../../lib/data-availability.js'
import { useServerClock } from '../../hooks/useServerClock.js'
import { Spinner } from '../common/Spinner.js'
import './AppShell.css'

// ─── Vues disponibles ─────────────────────────────────────────────────────────

type AppView = 'agenda' | 'templates' | 'clock' | 'programs' | 'premieres' | 'jingles' | 'media' | 'cg' | 'ads' | 'infoservice' | 'reports' | 'settings'

type NavGroup = 'Antenne' | 'Catalogue' | 'Pilotage'

interface NavItem {
  id:        AppView
  label:     string
  sublabel?: string
  icon:      React.ReactNode
  group:     NavGroup
  badge?: () => number | null   // badge dynamique (alertes, etc.)
  permission: Permission
  /** Visibilité personnalisée (droits combinés). Prioritaire sur `permission`. */
  visible?: () => boolean
}

const NAV_GROUPS: readonly NavGroup[] = ['Antenne', 'Catalogue', 'Pilotage']

// ─── Composant racine ─────────────────────────────────────────────────────────

export function AppLayout() {
  const [activeView, setActiveView] = useState<AppView>('agenda')
  // Onglet de Réglages demandé par un autre écran ; la clé remonte la vue
  // pour qu'une seconde demande rouvre l'onglet même si Réglages est affiché.
  const [settingsRequest, setSettingsRequest] = useState<{ tab: string; key: number } | null>(null)
  const [pendingAdd, setPendingAdd] = useState<{ programId: string; itemType: 'PROGRAM' | 'JINGLE' } | null>(null)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const { isMobileMode, setOverride } = useMobileOperatorMode()
  const { channels }    = useChannelList()
  const activeChannelId = useActiveChannelId()
  const wsConnected     = useWsConnected()
  const wsStateReady    = useWsStateReady()
  const wsEverReady     = useWsEverReady()
  const bridgeConnected = useBridgeConnected()
  const playout         = usePlayoutState()
  const unread          = useUnreadAlerts()
  const authUser        = useAuthUser()
  const permissions = useMemo(() => new Set(authUser?.permissions ?? []), [authUser?.permissions])
  const can = useCallback((permission: Permission) => authUser?.role === 'ADMIN' || permissions.has(permission), [authUser?.role, permissions])
  const realtimeRequired = can('GRID_VIEW')
  const { theme, toggleTheme } = useTheme()
  const clock = useServerClock()
  const [browserOnline, setBrowserOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine)

  useEffect(() => {
    const update = () => setBrowserOnline(navigator.onLine)
    window.addEventListener('online', update)
    window.addEventListener('offline', update)
    return () => {
      window.removeEventListener('online', update)
      window.removeEventListener('offline', update)
    }
  }, [])

  // WebSocket branché au niveau racine — une seule connexion pour toute l'app
  usePlayoutSync(can('GRID_VIEW') ? activeChannelId : null)

  // Items du jour d'antenne réel, montés une seule fois : le bandeau permanent
  // doit suivre la diffusion, pas la navigation dans la Grille.
  useLiveSchedule(can('GRID_VIEW') ? activeChannelId : null)

  const availability = dataAvailabilityState({
    browserOnline,
    realtimeRequired,
    wsConnected,
    wsStateReady,
    wsEverReady,
    backendStatus: clock.status,
  })
  const dataReady = availability === 'ready'
  const workspaceScope = realtimeRequired ? activeChannelId : 'server'
  const [lastReadyScope, setLastReadyScope] = useState<string | null>(null)
  useEffect(() => {
    if (dataReady && workspaceScope) setLastReadyScope(workspaceScope)
  }, [dataReady, workspaceScope])
  const workspaceAvailable = canPresentWorkspace(availability, workspaceScope, lastReadyScope)

  const isLiveViewer = window.location.pathname === '/watch'

  // Callback "Ajouter à la grille" depuis Programmes / Jingles
  const handleAddToGrid = useCallback((program: Program) => {
    const itemType = ANTENNA_ELEMENT_PROGRAM_TYPES.includes(program.type) ? 'JINGLE' : 'PROGRAM'
    setPendingAdd({ programId: program.id, itemType })
    setActiveView('agenda')
  }, [])

  // Un clic dans le rail ouvre Réglages sur son premier onglet, pas sur le
  // dernier onglet demandé par un autre écran.
  const handleSelectView = useCallback((view: AppView) => {
    setSettingsRequest(null)
    setActiveView(view)
  }, [])
  const handleChannelChange = useCallback((id: string) => useStore.getState().setActiveChannel(id), [])
  const handleOpenSearch = useCallback(() => setPaletteOpen(true), [])
  const handleCloseSearch = useCallback(() => setPaletteOpen(false), [])
  const handlePaletteNavigate = useCallback((target: PaletteTarget) => {
    setActiveView(target.view)
    if (target.programId) useStore.getState().setSelectedProgram(target.programId)
  }, [])
  // Ouvrir une fiche depuis le calendrier des inédits : même chemin que la
  // palette de recherche, la vue catalogue lisant la sélection dans le store.
  const handleOpenProgram = useCallback((programId: string) => {
    setActiveView('programs')
    useStore.getState().setSelectedProgram(programId)
  }, [])
  const handleGoMobile = useCallback(() => setOverride('mobile'), [setOverride])
  const handleExitMobile = useCallback(() => setOverride('desktop'), [setOverride])

  useEffect(() => {
    const onOpenSettingsTab = (event: Event) => {
      const tab = (event as CustomEvent<OpenSettingsTabDetail>).detail?.tab
      if (!tab) return
      setSettingsRequest(previous => ({ tab, key: (previous?.key ?? 0) + 1 }))
      setActiveView('settings')
    }
    window.addEventListener(OPEN_SETTINGS_TAB_EVENT, onOpenSettingsTab)
    return () => window.removeEventListener(OPEN_SETTINGS_TAB_EVENT, onOpenSettingsTab)
  }, [])

  // ⌘K / Ctrl+K ouvre la recherche globale depuis n'importe quelle vue.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== 'k' || !(event.metaKey || event.ctrlKey)) return
      event.preventDefault()
      setPaletteOpen(current => !current)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const navItems = useMemo<NavItem[]>(() => {
    const items: NavItem[] = [
    { id: 'agenda',    label: 'Grille',            icon: <CalendarDays size={16} />, group: 'Antenne', permission: 'GRID_VIEW',
      badge: () => (dataReady && unread > 0 ? unread : null) },
    { id: 'templates', label: 'Modèles de grille', icon: <LayoutGrid size={16} />,   group: 'Antenne', permission: 'GRID_TEMPLATE_VIEW' },
    { id: 'clock',     label: 'Clock',             icon: <Clock3 size={16} />,       group: 'Antenne', permission: 'CLOCK_VIEW', sublabel: 'Écrans régie et plateau' },
    { id: 'cg',        label: 'Habillage CG',      icon: <Layers size={16} />,       group: 'Antenne', permission: 'CG_VIEW' },
    { id: 'programs',  label: 'Programmes',        icon: <Film size={16} />,         group: 'Catalogue', permission: 'PROGRAM_VIEW' },
    { id: 'premieres', label: 'Calendrier inédits', icon: <CalendarCheck size={16} />, group: 'Catalogue', permission: 'PROGRAM_VIEW',
      sublabel: 'Prochaines 1res diffusions' },
    { id: 'jingles',   label: 'Éléments d’antenne', icon: <Music4 size={16} />,       group: 'Catalogue', permission: 'JINGLE_VIEW' },
    { id: 'media',     label: 'Médiathèque',       icon: <HardDrive size={16} />,    group: 'Catalogue', permission: 'PROGRAM_MANAGE',
      visible: () => can('PROGRAM_MANAGE') || can('JINGLE_MANAGE') || can('TECHNICAL_SETTINGS_MANAGE') },
    { id: 'ads',       label: 'Régie pub',         icon: <Megaphone size={16} />,    group: 'Catalogue', permission: 'ADS_VIEW' },
    { id: 'infoservice', label: 'Infoservice',     icon: <Images size={16} />,       group: 'Catalogue', permission: 'ADS_VIEW',
      sublabel: 'Annonces et diffusion' },
    { id: 'reports',   label: 'Rapports & AsRun',  icon: <BarChart3 size={16} />,    group: 'Pilotage', permission: 'REPORTS_VIEW_EXPORT' },
    { id: 'settings',  label: 'Réglages',          icon: <Settings size={16} />,     group: 'Pilotage', permission: 'TECHNICAL_SETTINGS_MANAGE',
      sublabel: 'Diagnostic playout, chaînes' },
    ]
    return items.filter(item => item.visible ? item.visible() : can(item.permission))
  }, [can, dataReady, unread])

  useEffect(() => {
    if (navItems.some(item => item.id === activeView)) return
    if (navItems[0]) setActiveView(navItems[0].id)
  }, [activeView, navItems])

  const engineLabel = useMemo(() => {
    const id = playout?.engineInstanceId
    return typeof id === 'string' && id ? `MOTEUR ${id.slice(0, 8).toUpperCase()}` : null
  }, [playout?.engineInstanceId])

  if (isLiveViewer) {
    // Le retour antenne plein écran n'affiche aucune donnée de conduite : le
    // masquer pendant une reconnexion WebSocket ne protégeait de rien et
    // détruisait le lecteur, donc le flux, à chaque micro-coupure. Le lecteur
    // porte désormais son propre état (LIVE / CONNEXION).
    return (
      <>
        <LiveViewer />
        <ToastContainer />
      </>
    )
  }

  if (isMobileMode) {
    return (
      <>
        {workspaceAvailable
          ? <MobileOperatorView onExitMobile={handleExitMobile} />
          : <ConnectionState state={availability} presentation="screen" onExitMobile={handleExitMobile} />}
        <ToastContainer />
        {workspaceAvailable && <OperatorAlertPopups />}
      </>
    )
  }

  // Un groupe dont aucune entrée n'est visible (permissions) ne doit pas laisser
  // son seul titre orphelin dans le rail — on l'omet complètement.
  const navGroups = NAV_GROUPS
    .map(label => ({ label, items: navItems.filter(item => item.group === label) }))
    .filter(group => group.items.length > 0)

  return (
    <div className="tp-shell">

      <AppHeader
        channels={channels}
        activeChannelId={activeChannelId}
        onChannelChange={handleChannelChange}
        onOpenSearch={handleOpenSearch}
        theme={theme}
        onToggleTheme={toggleTheme}
        wsConnected={wsConnected}
        bridgeConnected={bridgeConnected}
        engineLabel={dataReady ? engineLabel : null}
        activeTargetName={dataReady ? playout?.activeTargetName : null}
        backupHealthy={dataReady && playout?.targets && playout.targets.length > 1
          ? playout.targets.some(target => !target.active && target.healthy)
          : null}
        dataAvailability={availability}
        realtimeRequired={realtimeRequired}
      />

      {/* Bandeau antenne permanent : développé sur la Grille, compact ailleurs. */}
      {can('GRID_VIEW') && (dataReady
        ? <OnAirBanner channelId={activeChannelId} variant={activeView === 'agenda' ? 'expanded' : 'compact'} />
        : workspaceAvailable
          ? <ConnectionState
            state={availability}
            presentation="banner"
            compact={activeView !== 'agenda'}
            contentRetained
          />
          : null)}

      <div className="tp-shell__body">

        {/* ── Rail de navigation ────────────────────────────────────────────── */}
        <nav className="tp-sidebar" aria-label="Navigation principale">
          <div className="tp-nav-sections">
            {navGroups.map(group => (
              <section className="tp-nav-section" key={group.label} aria-label={group.label}>
                <div className="tp-nav-heading">{group.label}</div>
                {group.items.map(item => (
                  <NavButton
                    key={item.id}
                    item={item}
                    active={activeView === item.id}
                    onSelect={handleSelectView}
                  />
                ))}
              </section>
            ))}
          </div>

          <div className="tp-sidebar-footer">
            <button type="button" className="tp-button" title="Forcer le mode mobile" onClick={handleGoMobile} disabled={!can('PLAYOUT_CONTROL')}>
              <Smartphone size={14} aria-hidden="true" />
              <span className="tp-nav-label">Vue mobile</span>
            </button>
            <div className="tp-sidebar-version">
              v{__APP_VERSION__} · {(channels.find(c => c.id === activeChannelId)?.name ?? '—').toUpperCase()} · {import.meta.env.PROD ? 'PROD' : 'DEV'}
            </div>
          </div>
        </nav>

        {/* ── Vue active ────────────────────────────────────────────────────── */}
        <div className="tp-main" data-active-view={activeView}>
          {!workspaceAvailable && <ConnectionState state={availability} presentation="main" />}
          {workspaceAvailable && navItems.length === 0 && (
            <div className="tp-empty-state">
              <h2>Aucun accès attribué</h2>
              <p>Un administrateur doit vous attribuer au moins une fonctionnalité et une chaîne.</p>
            </div>
          )}
          {workspaceAvailable && activeView === 'agenda'   && (
            <AgendaView
              defaultAdd={pendingAdd ?? undefined}
              onDefaultAddConsumed={() => setPendingAdd(null)}
            />
          )}
          {workspaceAvailable && activeView === 'templates' && <GridTemplatesView />}
          {workspaceAvailable && activeView === 'clock'     && <ClockAdminView />}
          {workspaceAvailable && activeView === 'programs' && <ProgramsView onAddToGrid={handleAddToGrid} />}
          {workspaceAvailable && activeView === 'premieres' && <PremiereCalendarView onOpenProgram={handleOpenProgram} />}
          {workspaceAvailable && activeView === 'jingles'  && <JinglesView  onAddToGrid={handleAddToGrid} />}
          {workspaceAvailable && activeView === 'media'    && <MediaLibraryView />}
          {workspaceAvailable && activeView === 'cg'       && <CgOverlaysView />}
          {workspaceAvailable && activeView === 'ads'      && <AdsView />}
          {workspaceAvailable && activeView === 'infoservice' && <InfoserviceView />}
          {workspaceAvailable && activeView === 'reports'  && <ReportsView />}
          {workspaceAvailable && activeView === 'settings' && (
            <SettingsView key={settingsRequest?.key ?? 0} initialTab={settingsRequest?.tab} />
          )}
        </div>
      </div>

      {workspaceAvailable && paletteOpen && (
        <CommandPalette
          channelId={activeChannelId}
          onClose={handleCloseSearch}
          onNavigate={handlePaletteNavigate}
        />
      )}

      {/* Notifications toast — bas gauche, au-dessus de tout */}
      <ToastContainer />
      {workspaceAvailable && <OperatorAlertPopups />}
    </div>
  )
}

const CONNECTION_COPY: Record<Exclude<DataAvailabilityState, 'ready'>, { title: string; detail: string }> = {
  loading: {
    title: 'Chargement des données…',
    detail: 'Connexion au serveur et vérification des informations en cours.',
  },
  reconnecting: {
    title: 'Reconnexion au serveur…',
    detail: 'Les informations sont masquées jusqu’à leur actualisation complète.',
  },
  offline: {
    title: 'Connexion réseau perdue',
    detail: 'Les informations sont masquées pour ne pas présenter de données obsolètes.',
  },
  'clock-unavailable': {
    title: 'Synchronisation horaire interrompue',
    detail: 'Une mesure horaire valide est nécessaire avant d’afficher les informations temps réel.',
  },
  'server-unavailable': {
    title: 'Serveur TrafficPlay indisponible',
    detail: 'Reconnexion automatique en cours. Les informations seront rechargées au retour du serveur.',
  },
}

const ConnectionState = memo(function ConnectionState({
  state,
  presentation,
  compact = false,
  contentRetained = false,
  onExitMobile,
}: {
  state: DataAvailabilityState
  presentation: 'banner' | 'main' | 'screen'
  compact?: boolean
  contentRetained?: boolean
  onExitMobile?: () => void
}) {
  if (state === 'ready') return null
  const copy = CONNECTION_COPY[state]
  const waiting = state === 'loading' || state === 'reconnecting' || state === 'server-unavailable'
  const detail = contentRetained
    ? state === 'reconnecting'
      ? 'Flux temps réel interrompu. Les données affichées restent disponibles pendant la reconnexion.'
      : state === 'offline'
        ? 'Les données affichées restent disponibles, mais peuvent être obsolètes jusqu’au retour du réseau.'
        : state === 'clock-unavailable'
          ? 'Les données restent visibles, mais l’horloge et les décomptes peuvent être imprécis jusqu’à la reprise.'
          : 'Les données affichées restent disponibles pendant la reprise automatique.'
    : copy.detail
  return (
    <div
      className={`tp-connection-state tp-connection-state--${presentation}${compact ? ' is-compact' : ''}`}
      role="status"
      aria-live="polite"
    >
      <span className="tp-connection-state__icon" aria-hidden="true">
        {waiting ? <Spinner /> : <AlertTriangle size={20} />}
      </span>
      <span className="tp-connection-state__copy">
        <span className="tp-connection-state__title">{copy.title}</span>
        {!compact && <span className="tp-connection-state__detail">{detail}</span>}
      </span>
      {onExitMobile && (
        <button type="button" className="tp-button" onClick={onExitMobile}>Version bureau</button>
      )}
    </div>
  )
})

// ─── Bouton de navigation ─────────────────────────────────────────────────────

function NavButton({ item, active, onSelect }: {
  item:     NavItem
  active:   boolean
  onSelect: (id: AppView) => void
}) {
  const badge = item.badge?.()
  const handleClick = useCallback(() => onSelect(item.id), [onSelect, item.id])
  return (
    <button
      onClick={handleClick}
      title={item.sublabel ? `${item.label} — ${item.sublabel}` : item.label}
      data-view={item.id}
      className={`tp-nav-button${item.sublabel ? ' tp-nav-button--stacked' : ''}${active ? ' is-active' : ''}`}
      aria-current={active ? 'page' : undefined}
    >
      <span className="tp-nav-icon" aria-hidden="true">{item.icon}</span>
      <span className="tp-nav-label">
        {item.label}
        {item.sublabel && <span className="tp-nav-sublabel">{item.sublabel}</span>}
      </span>
      {badge !== null && badge !== undefined && badge > 0 && (
        <span className="tp-nav-badge">
          {badge > 9 ? '9+' : badge}
        </span>
      )}
    </button>
  )
}
